import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { ChevronDown, ChevronRight, Loader2, Search, Shield } from 'lucide-react';
import type { Permission, Module } from '../../services/security/roles.service';
import { RolesService } from '../../services/security/roles.service';
import { NotificationToast } from '../ui/NotificationToast';

interface RolePermissionsEditorProps {
  roleId: string;
  readOnly?: boolean;
}

const serializeSelection = (sel: Record<string, Set<string>>) =>
  JSON.stringify(
    Object.keys(sel)
      .sort()
      .map((k) => [k, Array.from(sel[k]).sort()]),
  );

const copySelection = (sel: Record<string, Set<string>>) => {
  const copy: Record<string, Set<string>> = {};
  Object.entries(sel).forEach(([k, v]) => (copy[k] = new Set(v)));
  return copy;
};

export const RolePermissionsEditor: React.FC<RolePermissionsEditorProps> = ({
  roleId,
  readOnly = false,
}) => {
  const [allModules, setAllModules] = useState<Module[]>([]);
  const [allPermissions, setAllPermissions] = useState<Permission[]>([]);
  const [loading, setLoading] = useState(true);
  const [selection, setSelection] = useState<Record<string, Set<string>>>({});
  const [initialSelection, setInitialSelection] = useState<Record<string, Set<string>>>({});
  const [initialAssignedIds, setInitialAssignedIds] = useState<Set<string>>(new Set());
  const [expandedModules, setExpandedModules] = useState<Set<string>>(new Set());
  const [search, setSearch] = useState('');
  const [saving, setSaving] = useState(false);
  const [notification, setNotification] = useState<{
    title: string;
    message: string;
    type: 'success' | 'error' | 'warning' | 'info';
  } | null>(null);

  const showNotification = (
    title: string,
    message: string,
    type: 'success' | 'error' | 'warning' | 'info',
  ) => setNotification({ title, message, type });

  const fetchRole = useCallback(async () => {
    const detailedRole = await RolesService.getRoleById(roleId);
    const sel: Record<string, Set<string>> = {};
    const assigned = new Set<string>();
    (detailedRole.modulos || []).forEach((m) => {
      assigned.add(m.id);
      sel[m.id] = new Set((m.permisos || []).map((p) => p.id));
    });
    setSelection(sel);
    setInitialSelection(copySelection(sel));
    setInitialAssignedIds(assigned);
  }, [roleId]);

  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      setLoading(true);
      try {
        const [modules, permissions] = await Promise.all([
          RolesService.getAvailableModules(),
          RolesService.getAllPermissions(),
        ]);
        if (cancelled) return;
        setAllModules((Array.isArray(modules) ? modules : []).filter((m) => m.activo !== false));
        const perms = Array.isArray(permissions)
          ? permissions
          : ((permissions as any)?.data ?? []);
        setAllPermissions(perms.filter((p: Permission) => p.activo !== false));
        await fetchRole();
      } catch (error) {
        console.error('Error cargando catálogos de permisos:', error);
        if (!cancelled) showNotification('Error', 'No se pudieron cargar módulos y permisos.', 'error');
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    load();
    return () => {
      cancelled = true;
    };
  }, [roleId, fetchRole]);

  const isDirty = useMemo(
    () => serializeSelection(selection) !== serializeSelection(initialSelection),
    [selection, initialSelection],
  );

  const visibleModules = useMemo(() => {
    const q = search.trim().toLowerCase();
    const sorted = [...allModules].sort((a, b) => a.orden - b.orden);
    if (!q) return sorted;
    return sorted.filter((m) => m.nombre.toLowerCase().includes(q));
  }, [allModules, search]);

  const toggleModuleExpansion = (moduleId: string) => {
    setExpandedModules((prev) => {
      const next = new Set(prev);
      if (next.has(moduleId)) next.delete(moduleId);
      else next.add(moduleId);
      return next;
    });
  };

  const togglePermission = (moduleId: string, permissionId: string) => {
    if (readOnly) return;
    setSelection((prev) => {
      const current = new Set(prev[moduleId] || []);
      if (current.has(permissionId)) current.delete(permissionId);
      else current.add(permissionId);
      return { ...prev, [moduleId]: current };
    });
  };

  const toggleModuleAll = (moduleId: string) => {
    if (readOnly) return;
    setSelection((prev) => {
      const current = prev[moduleId] || new Set<string>();
      const next =
        current.size === allPermissions.length
          ? new Set<string>()
          : new Set(allPermissions.map((p) => p.id));
      return { ...prev, [moduleId]: next };
    });
  };

  const handleReset = () => setSelection(copySelection(initialSelection));

  const handleSave = async () => {
    setSaving(true);
    try {
      for (const mod of allModules) {
        const target = selection[mod.id] || new Set<string>();
        const initial = initialSelection[mod.id] || new Set<string>();
        const wasAssigned = initialAssignedIds.has(mod.id);

        if (target.size === 0) {
          if (wasAssigned) {
            await RolesService.removeModuleFromRole(roleId, mod.id);
          }
          continue;
        }

        if (!wasAssigned) {
          await RolesService.assignModuleToRole(roleId, mod.id);
        }

        for (const pid of target) {
          if (!initial.has(pid)) {
            await RolesService.addPermissionToRoleModule(roleId, mod.id, pid);
          }
        }
        for (const pid of initial) {
          if (!target.has(pid)) {
            await RolesService.removePermissionFromRoleModule(roleId, mod.id, pid);
          }
        }
      }

      showNotification('¡Éxito!', 'Permisos actualizados correctamente.', 'success');
      await fetchRole();
    } catch (error) {
      console.error('Error guardando permisos:', error);
      showNotification(
        'Error',
        'Ocurrió un error al guardar. La vista se actualizó con el estado actual del servidor.',
        'error',
      );
      await fetchRole().catch(() => {});
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-6 h-6 animate-spin text-purple-600" />
        <span className="ml-2 text-gray-600 text-xs">Cargando módulos y permisos...</span>
      </div>
    );
  }

  return (
    <div>
      {/* Buscador */}
      <div className="relative mb-4">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
        <input
          type="text"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Buscar módulo..."
          className="w-full pl-9 pr-3 py-2 text-xs border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
        />
      </div>

      {visibleModules.length > 0 ? (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {visibleModules.map((mod) => {
            const selected = selection[mod.id] || new Set<string>();
            const total = allPermissions.length;
            const allChecked = total > 0 && selected.size === total;
            const isPartial = selected.size > 0 && selected.size < total;
            const isExpanded = expandedModules.has(mod.id);
            const disabled = saving || readOnly;

            return (
              <div
                key={mod.id}
                className="border border-gray-200 rounded-lg bg-white overflow-hidden"
              >
                <div className="flex items-center gap-3 px-4 py-3">
                  <IndeterminateCheckbox
                    checked={allChecked}
                    indeterminate={isPartial}
                    onChange={() => toggleModuleAll(mod.id)}
                    disabled={disabled}
                  />
                  <button
                    type="button"
                    onClick={() => toggleModuleExpansion(mod.id)}
                    className="flex-1 flex items-center justify-between text-left min-w-0"
                  >
                    <span className="text-xs font-medium text-gray-800 truncate">{mod.nombre}</span>
                    <span className="flex items-center gap-2 text-xs text-gray-500 shrink-0 ml-2">
                      {selected.size}/{total}
                      {isExpanded ? (
                        <ChevronDown className="w-4 h-4" />
                      ) : (
                        <ChevronRight className="w-4 h-4" />
                      )}
                    </span>
                  </button>
                </div>

                {isExpanded && (
                  <div className="border-t border-gray-100 bg-gray-50/50 px-4 py-3 grid grid-cols-2 gap-x-4 gap-y-2">
                    {allPermissions.map((p) => {
                      const checked = selected.has(p.id);
                      return (
                        <label
                          key={p.id}
                          className={`flex items-center gap-2 text-xs ${
                            disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'
                          }`}
                          title={p.descripcion}
                        >
                          <input
                            type="checkbox"
                            checked={checked}
                            onChange={() => togglePermission(mod.id, p.id)}
                            disabled={disabled}
                            className="h-3.5 w-3.5 rounded accent-purple-600"
                          />
                          <span
                            className={`truncate ${
                              checked ? 'text-gray-900 font-medium' : 'text-gray-600'
                            }`}
                          >
                            {p.nombre}
                          </span>
                        </label>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      ) : (
        <div className="text-center py-12 text-gray-500">
          <Search className="mx-auto h-10 w-10 text-gray-300 mb-3" />
          <p className="text-xs">No se encontraron módulos para tu búsqueda.</p>
        </div>
      )}

      {readOnly ? (
        <p className="mt-4 text-[11px] text-gray-400 flex items-center gap-1.5">
          <Shield className="w-3.5 h-3.5" />
          Solo lectura: no tienes permiso para modificar los permisos de este rol.
        </p>
      ) : (
        <>
          <p className="mt-4 text-[11px] text-gray-400">
            Marca o desmarca los permisos de cada módulo y guarda los cambios. El contador muestra
            permisos asignados sobre el total disponible.
          </p>
          <div className="sticky bottom-0 -mx-5 -mb-5 mt-4 px-5 py-3 bg-white border-t border-gray-200 flex justify-end gap-3">
            <button
              onClick={handleReset}
              disabled={!isDirty || saving}
              className="px-3 py-2 rounded-lg border border-gray-300 text-gray-700 text-xs font-medium hover:bg-gray-100 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Cancelar
            </button>
            <button
              onClick={handleSave}
              disabled={!isDirty || saving}
              className="flex items-center gap-2 px-3 py-2 rounded-lg bg-purple-600 text-white text-xs font-medium hover:bg-purple-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {saving && <Loader2 className="w-3.5 h-3.5 animate-spin" />}
              {saving ? 'Guardando...' : 'Guardar permisos'}
            </button>
          </div>
        </>
      )}

      {notification && (
        <NotificationToast
          isOpen={!!notification}
          title={notification.title}
          message={notification.message}
          type={notification.type}
          onClose={() => setNotification(null)}
          duration={7000}
        />
      )}
    </div>
  );
};

interface IndeterminateCheckboxProps {
  checked: boolean;
  indeterminate: boolean;
  onChange: () => void;
  disabled?: boolean;
}

const IndeterminateCheckbox: React.FC<IndeterminateCheckboxProps> = ({
  checked,
  indeterminate,
  onChange,
  disabled,
}) => {
  const ref = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (ref.current) {
      ref.current.indeterminate = indeterminate && !checked;
    }
  }, [indeterminate, checked]);

  return (
    <input
      ref={ref}
      type="checkbox"
      checked={checked}
      onChange={onChange}
      disabled={disabled}
      className="h-4 w-4 rounded accent-purple-600 cursor-pointer disabled:cursor-not-allowed"
    />
  );
};

export default RolePermissionsEditor;
